import React from 'react';
import PropTypes from 'prop-types';
import connection from '@src/connection';
import hashHistory from '@src/History';

import BotStore from '@src/store';

import Table from 'relements/build/esm/ui/table';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import Search from 'relements/build/esm/input/search';
import Loader from 'relements/build/esm/decorators/loader';

import { TEMPLATE_TYPES, AlertTypes } from 'constants/MogamboConstants';
import EditTemplateDetailsModal from 'containers/Templates/Modals/EditTemplateDetailsModal';
import ViewTemplateDetailsModal from 'containers/Templates/Modals/ViewTemplateDetailsModal';
import { getAllStories } from 'actions/StoryActions';

import { createNewStory } from 'actions/BotActions';

import { createAlert } from '@src/actions/_utils';

// Shared Components

import TableName from '@src/components/TableHelpers/TableName';
import TableLabel from '@src/components/TableHelpers/TableLabel';
import TableLabelWithIcon from '@src/components/TableHelpers/TableLabelWithIcon';
import styles from './Templates.scss';
import * as maps from './maps';

const COLUMNS = [
  {
    title: 'Smart Skill Name',
    width: '25%',
    sortKey: 'name',
  },
  {
    title: 'Published',
    width: '15%',
    sortKey: 'active',
  },
  {
    title: 'Type',
    // width: '300px',
    sortKey: 'template_type',
  },
  {
    title: 'Category',
    width: '15%',
    sortKey: 'tags',
  },
  {
    title: 'Author',
    width: '15%',
    sortKey: 'author',
  },
  // {
  //     title: 'Last Modified At',
  //     width: '10%',
  // },
  {
    title: '',
    width: '48px',
  },
];

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
export default class Templates extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sortKey: 'name',
      sortOrder: 1,
    };
  }

  componentDidMount() {
    if (this.props.canViewEditSmartSkill) {
      this.props.activateLoader();
      maps.getAllTemplatesForEditor(this.props.deactivateLoader);
    } else {
      createAlert(
        'You do not have permission to view this page. Redirecting...',
        5000,
        AlertTypes.NEGATIVE
      );
      setTimeout(() => {
        hashHistory.push(`/bots`);
      }, 2500);
    }
  }

  render() {
    const data = this._generateData(
      this.state.searchResults || this.props.allTemplates
    );
    return (
      <div className={styles.templates}>
        <div className={styles.templatesHeader}>
          <Search
            searchKeys={['name', 'tags', 'author']}
            options={this.props.allTemplates || []}
            onChange={this._onSearch}
            placeholder="search templates"
            autoFocus
            className={styles.templatesHeaderSearch}
          />
        </div>
        <div className={styles.templatesBody}>
          <Table
            simple
            sortable
            rows={data}
            columns={COLUMNS}
            sortKey={this.state.sortKey}
            sortOrder={this.state.sortOrder}
            onSort={this._sort}
            onRowClick={this._navToTemplate}
            prefixClassName={styles.templatesBodyTable}
            virtual
            rowHeight={48}
            height={600}
          />
        </div>
      </div>
    );
  }

  _renderContextMenuOptions = (bot) => (closeContextMenu) => {
    return (
      <>
        <ContextMenu.Item
          onClick={this._editTemplateDetails(bot, closeContextMenu)}
          iconType="edit"
          iconSize={18}
          title="Edit Smart Skill Details"
        />
        <ContextMenu.Item
          onClick={this._viewTemplateDetails(bot, closeContextMenu)}
          iconType="story"
          iconSize={18}
          title="About Smart Skill"
        />
      </>
    );
  };

  /**
   * It generates the entire tables data
   * First it sorts all the rows (templates) based on the sort column selected
   * but some of the sortkeys aren't simple strings so we need to normalize them
   *
   * For example, boolean values need to be converted to strings
   * It's case insensitive filtering so we lowercase the values as well
   *
   * Once sorted, it maps over all the rows (templates) and generates the different columns
   * for the rows
   * @memberof Templates
   */
  _generateData = (templates) => {
    const { sortKey, sortOrder } = this.state;
    return templates
      .sort((a, b) => {
        let aValue = a[sortKey] || '';
        let bValue = b[sortKey] || '';
        if (typeof aValue === 'boolean') {
          aValue = aValue ? '1' : '0';
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
        }
        if (typeof bValue === 'boolean') {
          bValue = bValue ? '1' : '0';
        } else if (typeof bValue === 'string') {
          bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) return -1 * sortOrder;
        if (aValue > bValue) return 1 * sortOrder;
        return 0;
      })
      .map((template) => {
        const row = {
          id: template.id,
          columns: [],
        };
        row.columns.push(
          this._generateColumn(<TableName>{template.name}</TableName>)
        );
        row.columns.push(
          this._generateColumn(
            <TableLabel>{template.active ? 'Yes' : 'No'}</TableLabel>
          )
        );
        row.columns.push(
          this._generateColumn(
            <TableLabelWithIcon
              iconType={TEMPLATE_TYPES[template.template_type].iconType}
            >
              {TEMPLATE_TYPES[template.template_type].title}
            </TableLabelWithIcon>
          )
        );
        row.columns.push(
          this._generateColumn(<TableLabel>{template.tags}</TableLabel>)
        );
        row.columns.push(
          this._generateColumn(<TableLabel>{template.author}</TableLabel>)
        );
        // row.push(this._generateColumn(<TableLabel>{template.modified_at}</TableLabel>));
        row.columns.push(
          this._generateColumn(
            <ContextMenu.Icon offset={{ top: -8, left: -100 }}>
              {this._renderContextMenuOptions(template)}
            </ContextMenu.Icon>
          )
        );
        return row;
      });
  };

  _generateColumn = (content) => ({
    content,
  });

  _editTemplateDetails = (template, closeContextMenu) => (e) => {
    closeContextMenu(e);
    maps.openComponentModal(
      <EditTemplateDetailsModal templateId={template.id} />,
      'Edit Smart Skill Details',
      null,
      'close'
    );
  };

  _viewTemplateDetails = (template, closeContextMenu) => (e) => {
    closeContextMenu(e);
    maps.openComponentModal(
      <ViewTemplateDetailsModal templateId={template.id} />,
      'About Smart Skill',
      null,
      'close'
    );
  };

  _sort = (column) => {
    const sortOrder =
      this.state.sortKey === column.sortKey ? -1 * this.state.sortOrder : 1;
    this.setState({ sortKey: column.sortKey, sortOrder });
  };

  _onSearch = (searchResults) => {
    this.setState({ searchResults, sortKey: '' });
  };

  getStoryToNavigate = (allStories) => {
    const storyToNavigate = allStories.find(
      (story) => !story.title.includes('Feedback')
    );
    return storyToNavigate || allStories[0];
  };

  createSkillset = (template) => {
    const domainId = template.domain_id;
    const domainName = template.name;

    createNewStory('Skillset-1', domainId, domainName, (response) => {
      hashHistory.push(
        `/v2/bots/${template.id}/stories/${response.story.id}/substories`
      );
    });
  };

  _navToTemplate = (templateIndex) => {
    const templates = this.state.searchResults || this.props.allTemplates;
    const template = templates[templateIndex];
    const templateDomainId = template.domain_id;

    getAllStories(templateDomainId, () => {
      BotStore.setIsSmartSkillSelectedViaPage(true);
      BotStore.emitChange();
      const allStories = BotStore.getAllStories().filter((item) => item.active);
      if (allStories.length > 0) {
        hashHistory.push(
          `/v2/bots/${templateDomainId}/stories/${
            this.getStoryToNavigate(allStories).id
          }/substories`
        );
      } else {
        // If the selected bot has no stories. Create one and navigate to that story.
        // This will only happen for old bots that doesn't have a substory. All the new bots will have a story by default.
        this.createSkillset(template);
      }
    });
  };
}

Templates.propTypes = {
  allTemplates: PropTypes.arrayOf(PropTypes.object),
  canViewEditSmartSkill: PropTypes.bool,
};
